/**
 * Helper functions for Monaco Editor operations
 * Migrated from Ace Editor helper functions
 */

// Store for decoration IDs to manage highlights
let decorationIds: string[] = [];
let highlightTimeouts: Map<string, NodeJS.Timeout> = new Map();

/**
 * Get the Monaco editor instance
 */
const getMonacoEditor = (): any => {
  try {
    return (window as any).__monaco_editor__;
  } catch (e) {
    console.warn('Monaco editor not available', e);
    return null;
  }
};

/**
 * Insert text at the current cursor position
 * @param text - Text to insert
 * @param linebreak - Whether to add a line break before/after
 */
export const insertTextAtCursor = (text: string, linebreak: boolean): void => {
  const editor = getMonacoEditor();
  if (!editor) {
    console.warn('Monaco editor not available for insertTextAtCursor');
    return;
  }

  try {
    const position = editor.getPosition();
    if (!position) return;

    const model = editor.getModel();
    if (!model) return;

    const carriageReturn = linebreak ? "\r\n" : "";
    let insertText = text;

    // If column > 0, add linebreak before, otherwise after
    if (position.column > 0) {
      insertText = carriageReturn + text;
    } else {
      insertText = text + carriageReturn;
    }

    // Use executeEdits for proper undo/redo support
    editor.executeEdits('insert-text', [{
      range: {
        startLineNumber: position.lineNumber,
        startColumn: position.column,
        endLineNumber: position.lineNumber,
        endColumn: position.column
      },
      text: insertText
    }]);

    // Move cursor after inserted text
    const newPosition = {
      lineNumber: position.lineNumber,
      column: position.column + insertText.length
    };
    editor.setPosition(newPosition);
    editor.focus();
  } catch (e) {
    console.error('Error inserting text at cursor', e);
  }
};

/**
 * Move cursor to a specific line and column
 * @param line - Line number (1-based)
 * @param column - Column number (1-based)
 */
export const moveCursorTo = (line: number, column: number): void => {
  const editor = getMonacoEditor();
  if (!editor) {
    console.warn('Monaco editor not available for moveCursorTo');
    return;
  }

  try {
    editor.setPosition({ lineNumber: line, column: column });
    editor.focus();
  } catch (e) {
    console.error('Error moving cursor', e);
  }
};

/**
 * Select text in the editor
 * @param startLine - Start line number (1-based)
 * @param startColumn - Start column number (1-based)
 * @param endLine - End line number (1-based)
 * @param endColumn - End column number (1-based)
 */
export const selectTextInEditor = (startLine: number, startColumn: number, endLine: number, endColumn: number): void => {
  const editor = getMonacoEditor();
  if (!editor) {
    console.warn('Monaco editor not available for selectTextInEditor');
    return;
  }

  try {
    editor.setSelection({
      startLineNumber: startLine,
      startColumn: startColumn,
      endLineNumber: endLine,
      endColumn: endColumn
    });
    editor.focus();
  } catch (e) {
    console.error('Error selecting text', e);
  }
};

/**
 * Undo the last editor action
 */
export const editorUndo = (): void => {
  const editor = getMonacoEditor();
  if (!editor) {
    console.warn('Monaco editor not available for editorUndo');
    return;
  }

  try {
    editor.trigger('keyboard', 'undo', null);
  } catch (e) {
    console.error('Error undoing editor action', e);
  }
};

/**
 * Get cursor position in pixels (relative to viewport)
 * @param row - Line number (1-based, but Monaco uses 1-based internally)
 * @param column - Column number (1-based)
 */
export const getCursorPixel = (row: number, column: number): { left: number; top: number } => {
  const editor = getMonacoEditor();
  if (!editor) {
    console.warn('Monaco editor not available for getCursorPixel');
    return { left: 0, top: 0 };
  }

  try {
    // Monaco uses 1-based line numbers, but the function might receive 0-based
    const lineNumber = row >= 1 ? row : row + 1;
    
    // Get the position in the editor
    const position = { lineNumber, column };
    
    // Get the pixel position using Monaco's coordinate conversion
    const domNode = editor.getDomNode();
    if (!domNode) {
      return { left: 0, top: 0 };
    }

    // Get the coordinate for the position using Monaco's API
    const coords = editor.getScrolledVisiblePosition(position);
    if (!coords) {
      return { left: 0, top: 0 };
    }

    // Monaco returns coordinates relative to the editor's scroll container
    // We need to get the actual pixel position relative to the viewport
    const editorContainer = domNode.querySelector('.monaco-scrollable-element') || domNode;
    if (!editorContainer) {
      return { left: 0, top: 0 };
    }

    const containerRect = (editorContainer as HTMLElement).getBoundingClientRect();
    
    // Monaco's getScrolledVisiblePosition returns coordinates relative to the scrollable element
    // We need to add the container's position relative to the viewport
    return {
      left: coords.left + containerRect.left,
      top: coords.top + containerRect.top
    };
  } catch (e) {
    console.error('Error getting cursor pixel position', e);
    return { left: 0, top: 0 };
  }
};

/**
 * Highlight a line in the editor
 * @param lineNumber - Line number to highlight (1-based)
 * @param highlightClass - CSS class name for highlighting
 * @param duration - Duration in milliseconds (0 = permanent)
 */
export const highlightLine = (lineNumber: number, highlightClass: string, duration: number = 3000): void => {
  const editor = getMonacoEditor();
  if (!editor) {
    console.warn('Monaco editor not available for highlightLine');
    return;
  }

  try {
    const model = editor.getModel();
    if (!model) return;

    // Monaco uses 1-based line numbers
    const line = lineNumber >= 1 ? lineNumber : lineNumber + 1;
    const lineCount = model.getLineCount();
    
    if (line < 1 || line > lineCount) {
      console.warn(`Line ${line} is out of range (1-${lineCount})`);
      return;
    }

    // Get line length
    const lineLength = model.getLineLength(line);
    
    // Map CSS classes to Monaco decoration types
    const decorationType = getDecorationTypeForClass(highlightClass);

    // Create decoration
    const decoration: any = {
      range: {
        startLineNumber: line,
        startColumn: 1,
        endLineNumber: line,
        endColumn: lineLength + 1
      },
      options: decorationType
    };

    // Add decoration
    const newDecorationIds = editor.deltaDecorations([], [decoration]);
    if (newDecorationIds.length > 0) {
      const id = newDecorationIds[0];
      decorationIds.push(id);

      // If duration is set, remove decoration after timeout
      if (duration > 0) {
        const timeoutId = setTimeout(() => {
          removeHighlight(id);
          highlightTimeouts.delete(id);
        }, duration);
        highlightTimeouts.set(id, timeoutId);
      }
    }
  } catch (e) {
    console.error('Error highlighting line', e);
  }
};

/**
 * Remove all highlights from the editor
 */
export const removeAllHighlights = (): void => {
  const editor = getMonacoEditor();
  if (!editor) {
    console.warn('Monaco editor not available for removeAllHighlights');
    return;
  }

  try {
    // Clear all timeouts
    highlightTimeouts.forEach((timeout) => clearTimeout(timeout));
    highlightTimeouts.clear();

    // Remove all decorations
    if (decorationIds.length > 0) {
      editor.deltaDecorations(decorationIds, []);
      decorationIds = [];
    }
  } catch (e) {
    console.error('Error removing highlights', e);
  }
};

/**
 * Remove a specific highlight by ID
 */
const removeHighlight = (id: string): void => {
  const editor = getMonacoEditor();
  if (!editor) return;

  try {
    decorationIds = decorationIds.filter(decId => decId !== id);
    editor.deltaDecorations([id], []);
  } catch (e) {
    console.error('Error removing highlight', e);
  }
};

/**
 * Map CSS class names to Monaco decoration options
 */
const getDecorationTypeForClass = (highlightClass: string): any => {
  const baseOptions: any = {
    isWholeLine: true,
    className: highlightClass,
    marginClassName: highlightClass
  };

  // Map specific classes to Monaco decoration styles
  switch (highlightClass) {
    case 'highlight-analyzing':
      return {
        ...baseOptions,
        className: 'monaco-highlight-analyzing',
        inlineClassName: 'monaco-highlight-analyzing',
        glyphMarginClassName: 'monaco-highlight-analyzing'
      };
    case 'highlight-modified':
      return {
        ...baseOptions,
        className: 'monaco-highlight-modified',
        inlineClassName: 'monaco-highlight-modified',
        glyphMarginClassName: 'monaco-highlight-modified'
      };
    case 'highlight-new':
      return {
        ...baseOptions,
        className: 'monaco-highlight-new',
        inlineClassName: 'monaco-highlight-new',
        glyphMarginClassName: 'monaco-highlight-new'
      };
    case 'highlight-deleted':
      return {
        ...baseOptions,
        className: 'monaco-highlight-deleted',
        inlineClassName: 'monaco-highlight-deleted',
        glyphMarginClassName: 'monaco-highlight-deleted'
      };
    case 'highlight-error':
      return {
        ...baseOptions,
        className: 'monaco-highlight-error',
        inlineClassName: 'monaco-highlight-error',
        glyphMarginClassName: 'monaco-highlight-error'
      };
    default:
      return {
        ...baseOptions,
        className: `monaco-${highlightClass}`,
        inlineClassName: `monaco-${highlightClass}`,
        glyphMarginClassName: `monaco-${highlightClass}`
      };
  }
};

/**
 * Request editor control (for compatibility with old Ace Editor code)
 * @param control - Control object with action and args
 */
export const requestAceControl = (control: any): void => {
  if (!control) return;
  console.log('requestAceControl (Monaco)', control);
  
  if (control.action === "moveCursor") {
    moveCursorTo(control.args[0], control.args[1]);
  } else if (control.action === "selectText") {
    selectTextInEditor(control.args[0], control.args[1], control.args[2], control.args[3]);
  }
};

/**
 * Mark editor as clean (no changes)
 * Note: This is a legacy function - Monaco editor tracks dirty state differently
 */
export const tidyEditor = (): void => {
  // Monaco editor doesn't use the same dirty state system as Ace
  // This function is kept for compatibility but may need to be implemented
  // based on your specific state management needs
  console.log('tidyEditor called (Monaco - no-op for now)');
};

